import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { gdprApi } from '../api/gdpr';
import { useAuth } from '../context/AuthContext';
import type { AdminUser } from '../types';

const roleLabel = (r: string) =>
  r === 'ADMIN' ? 'Administrator'
    : r === 'EMPLOYEE_ADMIN' ? 'Admin integratora'
    : r === 'EMPLOYEE' ? 'Integrator'
    : 'Klient';

const GdprPage: React.FC = () => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const search = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const { data } = await gdprApi.searchUsers(q);
      setUsers(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { search(query); }, 200);
    return () => clearTimeout(t);
  }, [query, search]);

  if (!user?.roles?.includes('ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  const downloadExport = async (u: AdminUser) => {
    setBusyUserId(u.id);
    setMessage(null);
    try {
      const { data } = await gdprApi.exportUser(u.id);
      const blob = data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gdpr-export-${u.username}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMessage({ kind: 'ok', text: `Eksport danych dla ${u.username} pobrany.` });
    } catch (e: any) {
      setMessage({ kind: 'err', text: 'Nie udało się wygenerować eksportu.' });
    } finally {
      setBusyUserId(null);
    }
  };

  const anonymize = async (u: AdminUser) => {
    const confirmed = window.prompt(
      `Anonimizacja konta "${u.username}" jest NIEODWRACALNA.\n\n`
      + `• imię, nazwisko, email, NIP zostaną wymazane\n`
      + `• hasło zostanie unieważnione (user nie będzie mógł się zalogować)\n`
      + `• ticketów przypisanych do tego usera zwolnimy do przydziału\n`
      + `• historia ticketów i notatek zostaje (jest własnością firmy)\n\n`
      + `Aby potwierdzić — wpisz dokładnie: ANONIMIZUJ`
    );
    if (confirmed !== 'ANONIMIZUJ') {
      setMessage({ kind: 'err', text: 'Anonimizacja anulowana.' });
      return;
    }
    setBusyUserId(u.id);
    setMessage(null);
    try {
      await gdprApi.anonymizeUser(u.id);
      setMessage({ kind: 'ok', text: `Konto ${u.username} zostało zanonimizowane.` });
      setSelected(null);
      await search(query);
    } catch (e: any) {
      setMessage({ kind: 'err', text: e?.response?.data?.message || 'Nie udało się zanonimizować konta.' });
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">RODO / GDPR</h1>
      </div>

      <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1rem', background: '#fef3c7', border: '1px solid #fcd34d' }}>
        <strong style={{ color: '#92400e' }}>⚠ Operacje wrażliwe</strong>
        <p style={{ margin: '0.375rem 0 0 0', fontSize: '0.875rem', color: '#78350f' }}>
          Eksport zwraca <em>wszystkie</em> dane osobowe użytkownika (profil, role, tickety, notatki, notyfikacje, audit).
          Anonimizacja jest nieodwracalna — zachowuje historię biznesową (treść ticketów, notatek)
          ale wymazuje wszystkie pola identyfikujące osobę. Każda operacja jest zapisywana w audit logu.
        </p>
      </div>

      <div className="form-group" style={{ maxWidth: 480 }}>
        <input
          className="form-input"
          type="text"
          placeholder="Szukaj po loginie, imieniu, nazwisku, NIP..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>

      {message && (
        <div className={message.kind === 'ok' ? 'auth-info' : 'auth-error'} style={{ marginBottom: '0.75rem' }}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : users.length === 0 ? (
        <div className="empty-state">Brak użytkowników pasujących do filtra.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {users.map((u) => {
            const busy = busyUserId === u.id;
            const isAnonymized = u.username.startsWith('anon-');
            return (
              <div key={u.id} className="card" style={{ padding: '0.875rem 1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 600 }}>
                      {u.firstName} {u.lastName}
                      {isAnonymized && (
                        <span style={{ marginLeft: 8, padding: '0.125rem 0.5rem', borderRadius: 4,
                          background: '#e0e7ff', color: '#3730a3', fontSize: '0.6875rem', fontWeight: 700 }}>
                          ZANONIMIZOWANY
                        </span>
                      )}
                    </div>
                    <div className="item-meta" style={{ fontSize: '0.8125rem' }}>
                      @{u.username}
                      {u.customerNip && <> · NIP {u.customerNip}</>}
                      {u.roles.length > 0 && <> · {u.roles.map(roleLabel).join(', ')}</>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => downloadExport(u)}
                      disabled={busy}
                    >
                      {busy ? '…' : '↓ Eksportuj JSON'}
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => anonymize(u)}
                      disabled={busy || isAnonymized}
                      style={{
                        background: isAnonymized ? '#e5e7eb' : '#fee2e2',
                        color: isAnonymized ? '#9ca3af' : '#991b1b',
                        border: `1px solid ${isAnonymized ? '#d1d5db' : '#fca5a5'}`,
                      }}
                      title={isAnonymized ? 'Konto już zanonimizowane' : 'Wymaż dane osobowe'}
                    >
                      {busy ? '…' : 'Anonimizuj'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GdprPage;
